import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Metric,
  Field,
  TextInput,
  VerificationBadge,
  FailClosedNotice,
} from "@odingard/design-system";
import { EmptyState, ErrorState, LoadingState } from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import type { AskResult, ProcessSolicitationResult } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { proposalApi } from "../../lib/api";
import { DemoModeBanner, LiveModeBadge, GroundedEvidence, CitationList } from "../../components/ProposalGrounded";

// Solicitation detail: processing status + process action, requirement extraction
// (compliance matrix), Section L instructions, Section M factors, and grounded Ask.
export function PaiSolicitation() {
  const { activeOrg } = useAuth();
  const { solicitationId = "" } = useParams();
  const orgId = activeOrg?.id ?? "";

  const solState = useAsync(async () => proposalApi.solicitation(orgId, solicitationId), [orgId, solicitationId]);
  const matrixState = useAsync(async () => proposalApi.complianceMatrix(orgId, solicitationId), [orgId, solicitationId]);
  const lState = useAsync(async () => proposalApi.sectionL(orgId, solicitationId), [orgId, solicitationId]);
  const mState = useAsync(async () => proposalApi.sectionM(orgId, solicitationId), [orgId, solicitationId]);

  const s = solState.data?.data;
  const demo = solState.data?.demoMode ?? false;
  const matrix = matrixState.data?.data;

  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<ProcessSolicitationResult | null>(null);
  const [processErr, setProcessErr] = useState<string | null>(null);

  async function process() {
    setProcessing(true);
    setProcessErr(null);
    try {
      const res = await proposalApi.processSolicitation(orgId, solicitationId);
      setProcessResult(res.data);
      solState.reload();
      matrixState.reload();
      lState.reload();
      mState.reload();
    } catch (e) {
      setProcessErr(e instanceof ApiError ? e.message : "Processing failed.");
    } finally {
      setProcessing(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{s?.title ?? "Solicitation"}</h1>
          <p>Processing status, requirement extraction, and grounded Q&amp;A.</p>
        </div>
        <div className="ods-row">
          <LiveModeBadge demoMode={demo} />
          {s && (
            <Link to={`/studio/proposal-ai/opportunities/${s.opportunityId}`}>
              <Button size="sm" variant="secondary">
                Opportunity
              </Button>
            </Link>
          )}
        </div>
      </div>

      <DemoModeBanner show={demo} />
      {solState.loading && <LoadingState label="Loading solicitation…" />}
      {solState.error && <ErrorState description={solState.error.message} onRetry={solState.reload} />}

      {s && (
        <>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={s.documentType} label="Document type" />
            </div>
            <div className="metric-card">
              <Metric value={s.uploadStatus} label="Upload status" />
            </div>
            <div className="metric-card">
              <Metric value={s.processingStatus} label="Processing status" />
            </div>
            <div className="metric-card">
              <Metric value={`v${s.versionNumber}`} label="Version" />
            </div>
          </div>

          {s.failureReason && <FailClosedNotice message={s.failureReason} />}

          <Card>
            <CardHeader title="Requirement extraction" />
            <CardBody>
              <div className="ods-stack">
                <p style={{ margin: 0, color: "var(--ods-color-muted)" }}>
                  Shred this solicitation into requirements, instructions, evaluation factors, deliverables, and a
                  compliance matrix.
                </p>
                {processErr && <FailClosedNotice message={processErr} />}
                <div>
                  <Button size="sm" onClick={process} disabled={processing}>
                    {processing ? "Processing…" : "Generate requirements"}
                  </Button>
                </div>
                {processResult && (
                  <div className="metric-grid">
                    <div className="metric-card">
                      <Metric value={processResult.requirements} label="Requirements" />
                    </div>
                    <div className="metric-card">
                      <Metric value={processResult.matrixRows} label="Matrix rows" />
                    </div>
                    <div className="metric-card">
                      <Metric value={processResult.gaps} label="Gaps" tone="var(--ods-color-warning)" />
                    </div>
                    <div className="metric-card">
                      <Metric value={processResult.bidRecommendation} label="Bid recommendation" />
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Compliance matrix */}
          <Card>
            <CardHeader
              title={`Compliance matrix${matrix ? ` · ${matrix.rows.length} requirements` : ""}`}
              action={
                <Button size="sm" variant="ghost" onClick={matrixState.reload}>
                  Refresh
                </Button>
              }
            />
            <CardBody>
              {matrixState.loading && <LoadingState label="Loading compliance matrix…" />}
              {matrixState.error && <ErrorState description={matrixState.error.message} onRetry={matrixState.reload} />}
              {matrix && matrix.rows.length === 0 && (
                <EmptyState title="No requirements yet" description="Generate requirements to build the matrix." />
              )}
              {matrix && matrix.rows.length > 0 && (
                <div className="ods-stack">
                  {matrix.rows.map((r) => (
                    <div key={r.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--ods-color-line)" }}>
                      <div
                        className="ods-row"
                        style={{ justifyContent: "space-between", flexWrap: "wrap", gap: "var(--ods-space-2)" }}
                      >
                        <div style={{ fontWeight: 600 }}>
                          <Badge tone="neutral">§{r.sourceSection || r.requirementType}</Badge> {r.identifier}
                        </div>
                        <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                          <Badge tone={r.coverageStatus === "covered" ? "success" : "warning"}>{r.coverageStatus}</Badge>
                          <Badge tone={r.criticality === "high" ? "warning" : "neutral"}>{r.criticality}</Badge>
                          <VerificationBadge state={r.verificationState} />
                        </div>
                      </div>
                      <p style={{ margin: "6px 0 0", color: "var(--ods-color-muted)" }}>{r.requirementText}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Section L / M */}
          <div className="ods-row" style={{ gap: "var(--ods-space-3)", alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 320 }}>
              <Card>
                <CardHeader title="Section L: Instructions" />
                <CardBody>
                  {lState.loading && <LoadingState label="Loading Section L…" />}
                  {lState.error && <ErrorState description={lState.error.message} onRetry={lState.reload} />}
                  {lState.data && lState.data.data.instructions.length === 0 && (
                    <EmptyState title="No instructions" description="Process the solicitation to extract Section L." />
                  )}
                  {lState.data && lState.data.data.instructions.length > 0 && (
                    <ul className="ods-stack" style={{ margin: 0, paddingLeft: 18 }}>
                      {lState.data.data.instructions.map((i) => (
                        <li key={i.id}>
                          <strong>{i.identifier}</strong>: {i.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            </div>
            <div style={{ flex: 1, minWidth: 320 }}>
              <Card>
                <CardHeader title="Section M: Evaluation factors" />
                <CardBody>
                  {mState.loading && <LoadingState label="Loading Section M…" />}
                  {mState.error && <ErrorState description={mState.error.message} onRetry={mState.reload} />}
                  {mState.data && mState.data.data.factors.length === 0 && (
                    <EmptyState title="No factors" description="Process the solicitation to extract Section M." />
                  )}
                  {mState.data && mState.data.data.factors.length > 0 && (
                    <ul className="ods-stack" style={{ margin: 0, paddingLeft: 18 }}>
                      {mState.data.data.factors.map((f) => (
                        <li key={f.id}>
                          <strong>{f.identifier}</strong>: {f.text}
                          {f.weight ? <Badge tone="neutral"> {f.weight}</Badge> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>

          <AskCard orgId={orgId} solicitationId={solicitationId} />
        </>
      )}
    </>
  );
}

// Grounded Q&A over the solicitation. Fails closed: when the backend cannot
// ground an answer, no generated text is shown.
function AskCard({ orgId, solicitationId }: { orgId: string; solicitationId: string }) {
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState<AskResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function ask() {
    if (!question.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await proposalApi.askSolicitation(orgId, solicitationId, question.trim());
      setResult(res.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Request failed.");
    } finally {
      setLoading(false);
    }
  }

  const grounded = result?.grounded && result.answer;

  return (
    <Card>
      <CardHeader title="Ask the Solicitation" />
      <CardBody>
        <div className="ods-stack">
          <Field label="Question">
            {(id) => (
              <TextInput
                id={id}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="e.g. What is the technical volume page limit?"
              />
            )}
          </Field>
          <div>
            <Button size="sm" onClick={ask} disabled={loading || !question.trim()}>
              {loading ? "Asking…" : "Ask"}
            </Button>
          </div>
          {error && <FailClosedNotice message={error} />}
          {result && !grounded && (
            <FailClosedNotice message="No grounded answer is available for this question from the solicitation. Try rephrasing or upload the relevant document." />
          )}
          {result && grounded && (
            <div className="ods-stack">
              <p style={{ margin: 0 }}>{result.answer}</p>
              <GroundedEvidence meta={result} />
              <CitationList citations={result.citations ?? []} />
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
